/**
 * DlqPanel — Displays DLQ (Dead Letter Queue) monitoring information.
 *
 * Renders:
 * - DLQ count badge (red if > 0, green if 0)
 * - Empty state indicator when no failed messages
 * - Semantic HTML table with columns: Source Function, Error Message, Page ID, Arrived At
 * - Expandable rows showing full message body (raw JSON)
 * - Purge button with Radix AlertDialog confirmation
 * - Loading skeleton and error/retry states
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 3.1, 3.2, 3.3, 3.4, 3.5, 10.3, 10.6
 */

import React, { useState } from 'react';
import type { DlqResponse } from '../../types/ai-pipeline';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

export interface DlqPanelProps {
  readonly dlq: DlqResponse | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRefresh: () => void;
  readonly onPurge: () => Promise<void>;
}

function formatLocalTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function DlqPanel({
  dlq,
  isLoading,
  error,
  onRefresh,
  onPurge,
}: DlqPanelProps): React.ReactElement {
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const [isPurging, setIsPurging] = useState(false);

  const handlePurge = async (): Promise<void> => {
    setIsPurging(true);
    try {
      await onPurge();
    } finally {
      setIsPurging(false);
    }
  };

  const toggleExpand = (messageId: string): void => {
    setExpandedMessageId((prev) => (prev === messageId ? null : messageId));
  };

  // Loading state
  if (isLoading && !dlq) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading DLQ data">
        <div className="h-6 w-32 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  // Error state
  if (error && !dlq) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4" role="alert" aria-live="assertive">
        <p className="text-sm text-destructive">{error}</p>
        <button
          type="button"
          className="mt-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onRefresh}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!dlq) {
    return <div />;
  }

  const { approximateCount, messages, fetchedAt } = dlq;
  const hasMessages = approximateCount > 0;

  return (
    <div className="space-y-4">
      {/* DLQ count badge */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">Dead Letter Queue</span>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            hasMessages
              ? 'bg-red-100 text-red-800'
              : 'bg-green-100 text-green-800'
          }`}
          aria-label={`${approximateCount} messages in queue`}
        >
          {approximateCount}
        </span>
      </div>

      {/* Empty state */}
      {!hasMessages && (
        <div className="rounded-md border border-green-200 bg-green-50 p-4" role="status">
          <p className="text-sm text-green-800">✓ No failed messages in the queue</p>
        </div>
      )}

      {/* Message table */}
      {messages.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="Dead letter queue messages">
            <thead>
              <tr className="border-b">
                <th scope="col" className="px-2 py-2 text-left font-medium">Source Function</th>
                <th scope="col" className="px-2 py-2 text-left font-medium">Error Message</th>
                <th scope="col" className="px-2 py-2 text-left font-medium">Page ID</th>
                <th scope="col" className="px-2 py-2 text-left font-medium">Arrived At</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <React.Fragment key={message.messageId}>
                  <tr
                    className="border-b cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleExpand(message.messageId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpand(message.messageId);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={expandedMessageId === message.messageId}
                    aria-label={`Message from ${message.sourceFunction}, click to ${expandedMessageId === message.messageId ? 'collapse' : 'expand'} details`}
                  >
                    <td className="px-2 py-2 font-mono text-xs">{message.sourceFunction}</td>
                    <td className="px-2 py-2 text-xs text-muted-foreground max-w-xs truncate">
                      {message.errorMessage}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">{message.pageId ?? '\u2014'}</td>
                    <td className="px-2 py-2 text-xs">{formatLocalTime(message.arrivedAt)}</td>
                  </tr>
                  {expandedMessageId === message.messageId && (
                    <tr className="border-b bg-muted/30">
                      <td colSpan={4} className="px-4 py-3">
                        <p className="mb-1 text-xs font-medium text-muted-foreground">Message Body</p>
                        <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs font-mono whitespace-pre-wrap break-all">
                          {message.body}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Purge button with confirmation dialog */}
      {hasMessages && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              disabled={isPurging}
              aria-label="⚠ Destructive action: Purge all DLQ messages"
            >
              {isPurging && (
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {isPurging ? 'Purging...' : 'Purge All Messages'}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Purge all DLQ messages?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. All {approximateCount} messages in the dead letter queue will
                be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handlePurge}
                disabled={isPurging}
                aria-label="⚠ Destructive action: Confirm purge"
              >
                {isPurging && (
                  <svg
                    className="mr-2 h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {isPurging ? 'Purging...' : 'Purge All'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Refresh button and last updated */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          onClick={onRefresh}
          disabled={isLoading}
        >
          Refresh
        </button>
        <p className="text-xs text-muted-foreground">
          Last updated: {formatLocalTime(fetchedAt)}
        </p>
      </div>
    </div>
  );
}
